import React from "react";
import {
  Box,
  Card,
  Typography,
  Checkbox,
  BottomNavigation,
  BottomNavigationAction,
  Divider,
  Radio,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { motion, AnimatePresence } from "framer-motion";
import Exercise from "./Exercise";
import WorkoutMenu from "./WorkoutMenu";
import WorkoutTitle from "./WorkoutTitle";
import NavBar from "../NavBar";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import AddIcon from "@mui/icons-material/Add";
import ProgressBar from "../shared/ProgressBar";
import InsightsIcon from "@mui/icons-material/Insights";
import WorkoutStats from "./WorkoutStats";
import WorkoutShareDialog from "../shared/WorkoutShareDialog";
import LoginRegisterModal from "../LoginRegisterModal";
import AdminCopyWorkoutToUserModal from "../Admin/AdminCopyWorkoutToUserModal";

const Workout = ({
  workout,
  workoutActivities,
  workoutStats,
  bottomNavigationState,
  selectedIdForEdit,
  activityForEdit,
  onCheckBoxChange,
  onEditActivity,
  onEditSelectChange,
  onCopyWorkout,
  onBottomNavChange,
  onDeleteWorkout,
  onFinishWorkout,
  onShareWorkout,
  onCreateTemplate,
  onAddExercise,
  onAddRun,
  onAddRow,
  isPublic = false,
  shareDialogOpen = false,
  shareLink = "",
  handleCloseShareDialog,
  loginRegisterOpen,
  handleOpenLoginRegister,
  handleCloseLoginRegister,
  handleAdminCopyWorkoutToUser,
  adminCopyWorkoutToUserModalOpen,
  handleAdminSubmitCopyWorkoutToUser,
  handleAdminDeleteWorkoutFromUser,
  handleCloseCopyWorkoutToUserModal
}) => {
  const theme = useTheme();

  const getActivityId = (activity) => {
    switch (activity.type) {
      case "exercise":
        return activity.data.exercise_id;
      case "run":
        return activity.data.run_id;
      case "row":
        return activity.data.row_id;
      default:
        return null;
    }
  };

  return (
    <Box sx={{ width: "100%", height: "100%" }}>
      <Box
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-start",
          background: theme.palette.background.default,
        }}
      >
        <Box style={{ width: "100%" }}>
          <NavBar isMenuDisabled={isPublic} isInstallable={!isPublic} />
        </Box>
        <Box
          component={motion.div}
          layout
          transition={{ type: "spring", stiffness: 60, damping: 20 }}
          sx={{
            display: "flex",
            justifyContent: "flex-start",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Card
            sx={{
              background: theme.palette.background.paper,
              borderRadius: 4,
              p: 4,
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
              width: 300,
              height: "auto",
              mx: "auto",
              my: 4,
              position: "relative",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "5px",
                background: `linear-gradient(90deg, #F2E600 0%, ${theme.palette.tertiary.main} 100%)`,
              },
            }}
            component={motion.div}
            layout
          >
            {workout && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  paddingBottom: "16px",
                }}
              >
                <WorkoutTitle />
                <WorkoutMenu
                  handleCopyWorkout={onCopyWorkout}
                  handleDeleteWorkout={onDeleteWorkout}
                  handleFinishWorkout={onFinishWorkout}
                  handleCreateTemplateFromWorkout={onCreateTemplate}
                  handleShareWorkout={onShareWorkout}
                  handleAdminCopyWorkoutToUser={handleAdminCopyWorkoutToUser}
                />
              </Box>
            )}

            <AnimatePresence>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-start",
                  alignItems: "center",
                  flexDirection: "column",
                  textAlign: "left",
                  width: "100%",
                  maxHeight:
                    bottomNavigationState === 0 && selectedIdForEdit
                      ? "64px"
                      : "440px",
                  overflowY: "auto",
                  "&::-webkit-scrollbar": {
                    width: "8px",
                    height: "64px",
                  },
                  "&::-webkit-scrollbar-track": {
                    background: "#2A2A2E",
                    borderRadius: "10px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: "#6E6E73",
                    borderRadius: "10px",
                    "&:hover": {
                      background: "#A0A0A0",
                    },
                  },
                }}
              >
                {bottomNavigationState !== 3 && bottomNavigationState !== 4 &&
                  workoutActivities.map((activity, index) => {
                    if (
                      selectedIdForEdit &&
                      getActivityId(activity) !== selectedIdForEdit
                    ) {
                      return null;
                    }
                    return (
                      <Box
                        key={index}
                        sx={{
                          justifyContent: "space-between",
                          alignItems: "center",
                          display: "flex",
                          flexDirection: "row",
                          width: "100%",
                          paddingBottom: "4px",
                          paddingTop: "4px",
                        }}
                      >
                        <Typography variant="body1">
                          {activity.parsed}
                        </Typography>
                        {bottomNavigationState === 2 && (
                          <Checkbox
                            onChange={(e) =>
                              onCheckBoxChange(
                                activity,
                                e.target.checked,
                                index
                              )
                            }
                            checked={activity.completed}
                          />
                        )}
                        {bottomNavigationState === 0 && (
                          <Radio
                            checked={
                              selectedIdForEdit === getActivityId(activity)
                            }
                            onClick={() => onEditSelectChange(activity)}
                            name={activity.parsed}
                            slotProps={{ input: { "aria-label": activity.parsed } }}
                          />
                        )}
                      </Box>
                    );
                  })}
              </Box>

              {bottomNavigationState === 4 && (
                <WorkoutStats stats={workoutStats} />
              )}

              {bottomNavigationState === 3 && (
                <Exercise
                  addRow={onAddRow}
                  addExercise={onAddExercise}
                  addRun={onAddRun}
                  workoutActivities={workoutActivities}
                  isEdit={false}
                  isPublic={isPublic}
                />
              )}

              {bottomNavigationState === 0 && selectedIdForEdit && (
                <Exercise
                  handleEditActivity={onEditActivity}
                  workoutActivities={workoutActivities}
                  activityForEdit={activityForEdit}
                  isEdit={true}
                  isPublic={isPublic}
                />
              )}

              {bottomNavigationState === 2 && workoutActivities.length > 0 && (
                <>
                  <Divider sx={{ marginTop: "8px", marginBottom: "8px" }} />
                  <ProgressBar
                    max={100}
                    value={
                      (workoutActivities.filter((a) => a.completed).length /
                        workoutActivities.length) *
                      100
                    }
                    color={theme.palette.primary.main}
                    label=""
                    showPercentage={true}
                  />
                </>
              )}
            </AnimatePresence>
          </Card>

          <BottomNavigation
            showLabels
            value={"walue"}
            onChange={(e, newVal) => onBottomNavChange(newVal)}
            sx={{ borderRadius: "16px", marginTop: "16px" }}
          >
            <BottomNavigationAction id="Edit" label="Edit" icon={<EditIcon />} />
            <BottomNavigationAction id="View" label="View" icon={<VisibilityIcon />} />
            <BottomNavigationAction id="Complete" label="Complete" icon={<CheckBoxIcon />} />
            <BottomNavigationAction id="Add" label="Add" icon={<AddIcon />} />
            <BottomNavigationAction id="Stats" label="Stats" icon={<InsightsIcon />} />
          </BottomNavigation>

          <LoginRegisterModal
             open={loginRegisterOpen}
             handleOpen={handleOpenLoginRegister}
             handleClose={handleCloseLoginRegister}
          />

          <WorkoutShareDialog
            open={shareDialogOpen}
            handleClose={handleCloseShareDialog}
            link={shareLink}
          />

          <AdminCopyWorkoutToUserModal
            handleClose={handleCloseCopyWorkoutToUserModal}
            handleAdminDeleteWorkoutFromUser={handleAdminDeleteWorkoutFromUser} 
            handleAdminSubmitCopyWorkoutToUser={handleAdminSubmitCopyWorkoutToUser} 
            open={adminCopyWorkoutToUserModalOpen} 
          />
        </Box>
      </Box>
    </Box>
  );
};

export default Workout;
